// ═══════════════════════════════════════════════════════════════════════
// BUILD STORAGE — localStorage-backed persistence for Architect builds
// ═══════════════════════════════════════════════════════════════════════
// Saves the prospect's in-progress (or completed) Architect build so they
// can return tomorrow and pick up where they left off. Each build is keyed
// by a unique ID and includes the full ArchitectState + chat history +
// metadata. My Operating System reads the index to render a list; Build.tsx reads/writes
// individual builds.
//
// Storage strategy: one localStorage key per build (`sloe.build.<id>`),
// plus an index key (`sloe.builds.index`) that holds the lightweight
// listing for My Operating System. This avoids parsing all builds when My Operating System just
// wants to render the list.
//
// Limits: localStorage is typically 5-10MB per origin. Each build is
// expected to be 5-50KB depending on conversation length. We cap at 20
// builds and prune oldest when exceeded.
// ═══════════════════════════════════════════════════════════════════════

const INDEX_KEY = "sloe.builds.index";
const BUILD_KEY_PREFIX = "sloe.build.";
const MAX_BUILDS = 20;

// Lightweight summary used by My Operating System — small, parses fast, no chat history.
export interface SavedBuildSummary {
  id: string;
  company_name: string;
  industry: string;
  inferredStyle: string;       // shape style for icon/color rendering
  status: BuildStatus;
  created_at: number;          // epoch ms
  updated_at: number;          // epoch ms
  message_count: number;
}

// Pre-signup Guided Intake Wizard draft. During intake, architectState is still
// null — these raw wizard fields are the ONLY record of a cold user's input, so
// they're persisted separately to make a mid-wizard reload rehydrate where they
// left off. Optional: builds that reached the conversation have no intakeDraft.
export interface IntakeDraft {
  intakeStep: number;
  intakeCompany: string;
  intakeCountry: string | null;
  intakeContext: string;
  intakeDesc: string;
  intakeTrackedNoun: string;
  intakeStages: string;
}

// Full saved build — heavy, only loaded when resuming a single build.
export interface SavedBuild extends SavedBuildSummary {
  // Complete payloads — keep this opaque to the storage layer
  architectState: any;          // ArchitectState (defined in Build.tsx — keeping any to avoid circular import)
  messages: any[];              // ChatMessage[]
  inheritedDemoId: string | null; // if the build started from a specific demo
  mode?: "codegen" | "schema";  // persisted build path (Phase 1 Half-2) — lets a
                                // refresh/deep-link keep codegen mode with no ?mode param.
                                // Optional: pre-existing builds have none → URL fallback.
  // Pre-signup intake wizard draft — present only while status === "intake".
  // Cleared implicitly once the build advances (post-intake saves omit it).
  intakeDraft?: IntakeDraft;
}

export type BuildStatus =
  | "intake"          // still in 3-step wizard
  | "in_conversation" // talking to Architect, no preview yet
  | "preview"         // build_permission_granted, Operating System rendered
  | "booked";         // ready_to_book = true

// Generate a fresh build ID. Includes timestamp for sorting + random suffix
// to avoid collisions if two builds start in the same millisecond.
export function newBuildId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 7);
  return `b${ts}-${rand}`;
}

// Read the index. Returns empty array if missing or corrupt.
export function listBuilds(): SavedBuildSummary[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

// Load a single build by id. Null if not found.
export function loadBuild(id: string): SavedBuild | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(BUILD_KEY_PREFIX + id);
    if (!raw) return null;
    return JSON.parse(raw) as SavedBuild;
  } catch {
    return null;
  }
}

// Save (insert or update) a build. Updates the index automatically.
// Auto-prunes oldest builds if exceeding MAX_BUILDS.
export function saveBuild(build: SavedBuild): void {
  if (typeof window === "undefined") return;
  try {
    // Write the full build
    localStorage.setItem(BUILD_KEY_PREFIX + build.id, JSON.stringify(build));

    // Update the index
    const index = listBuilds();
    const summary: SavedBuildSummary = {
      id: build.id,
      company_name: build.company_name,
      industry: build.industry,
      inferredStyle: build.inferredStyle,
      status: build.status,
      created_at: build.created_at,
      updated_at: build.updated_at,
      message_count: build.message_count,
    };

    const existingIdx = index.findIndex((b) => b.id === build.id);
    if (existingIdx >= 0) {
      index[existingIdx] = summary;
    } else {
      index.push(summary);
    }

    // Sort newest-updated first
    index.sort((a, b) => b.updated_at - a.updated_at);

    // Prune oldest if over limit
    if (index.length > MAX_BUILDS) {
      const toRemove = index.slice(MAX_BUILDS);
      for (const old of toRemove) {
        localStorage.removeItem(BUILD_KEY_PREFIX + old.id);
      }
      index.length = MAX_BUILDS;
    }

    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch (err) {
    // localStorage might be full or disabled — fail silently rather than crash
    console.warn("Failed to save build:", err);
  }
}

// Delete a single build by id. Updates the index.
export function deleteBuild(id: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(BUILD_KEY_PREFIX + id);
    const index = listBuilds().filter((b) => b.id !== id);
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {
    // ignore
  }
}

// Derive status from architectState fields. Centralized so Build.tsx and
// My Operating System agree on what "preview" or "booked" means.
export function deriveStatus(architectState: any, intakeStep: number): BuildStatus {
  if (architectState?.ready_to_book) return "booked";
  if (architectState?.build_permission_granted) return "preview";
  if (intakeStep > 0) return "intake";
  return "in_conversation";
}

// Simulates syncing to cloud. Called by Build.tsx when crossing gates.
export async function syncToCloud(state: any, messages: any[]): Promise<void> {
  console.log("[Cloud Sync]: Synchronizing Architect State to the mainframe...");
  // In a real app, this would be a fetch/axios call to a backend
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log("[Cloud Sync]: Synced successfully.");
      resolve();
    }, 800);
  });
}
